import { useEffect, useState } from "react";
import { X, Save, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import DateInput from "../DateInput";
import CheckoutFields from "../vehicle-detail/CheckoutFields";
import PortFerryCard from "./PortFerryCard";
import { toast } from "sonner";

/**
 * Admin edit modal. Reuses the same server-authoritative pricing engine
 * as new bookings via `PUT /api/admin/bookings/{id}` — the backend
 * recomputes total whenever dates/vehicle/extras change.
 *
 * Extras are dynamically scoped to the selected vehicle's Car Group when
 * `allowed_extra_ids` is populated (falls back to all active extras).
 */
const PAYMENT_STATUSES = [
  { value: "unpaid",  label: "Pending" },
  { value: "paid",    label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "cod",     label: "Cash on Delivery" },
];
const PAYMENT_METHODS = [
  { value: "",                label: "— None —" },
  { value: "cash",            label: "Cash" },
  { value: "bank_transfer",   label: "Bank Transfer" },
  { value: "card_on_office",  label: "Card on Office" },
  { value: "paypal",          label: "PayPal" },
  { value: "stripe",          label: "Stripe" },
];
const STATUSES = ["pending", "confirmed", "active", "pending_payment", "completed", "cancelled"];
const DEFAULT_SOURCES = ["internet", "ai_agent", "direct", "agency", "hotel", "other"];

export default function EditBookingModal({ open, booking, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [extras, setExtras] = useState([]);
  const [business, setBusiness] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Init form snapshot when booking changes.
  useEffect(() => {
    if (!open || !booking) return;
    setError("");
    setForm({
      full_name:       booking.full_name || "",
      phone:           booking.phone || "",
      license_number:  booking.license_number || "",
      id_passport:     booking.id_passport || "",
      user_email:      booking.user_email || "",
      driver_age:      booking.driver_age ?? "",
      pickup_date:     booking.pickup_date || "",
      return_date:     booking.return_date || "",
      pickup_time:     booking.pickup_time || "10:00",
      return_time:     booking.return_time || "10:00",
      pickup_location: booking.pickup_location || "",
      return_location: booking.return_location || "",
      vehicle_id:      booking.vehicle_id || "",
      addons:          Array.isArray(booking.addons) ? booking.addons : [],
      source:          booking.source || "internet",
      status:          booking.status || "confirmed",
      payment_status:  booking.payment_status || "unpaid",
      payment_method:  booking.payment_method || "",
      paid_amount:     booking.paid_amount ?? 0,
      notes:           booking.notes || "",
      // Extra checkout fields — mirrored from the persisted booking so
      // the airport / port / hotel groups can render + be edited here.
      address:               booking.address || "",
      country:               booking.country || "",
      city:                  booking.city || "",
      zip:                   booking.zip || "",
      airline_company:       booking.airline_company || "",
      flight_number:         booking.flight_number || "",
      flight_time:           booking.flight_time || "",
      vessel_company:        booking.vessel_company || "",
      vessel_name:           booking.vessel_name || "",
      vessel_arrival_time:   booking.vessel_arrival_time || "",
      accommodation_name:    booking.accommodation_name || "",
      accommodation_address: booking.accommodation_address || "",
      // v3.7.1 — Port pickup ferry details card.
      ferry_details: {
        company:      booking.ferry_details?.company      || "",
        vessel_name:  booking.ferry_details?.vessel_name  || "",
        arrival_time: booking.ferry_details?.arrival_time || "",
      },
    });
  }, [open, booking]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/vehicles").catch(() => ({ data: [] })),
      api.get("/settings/locations").catch(() => ({ data: [] })),
      api.get("/settings/public").catch(() => ({ data: {} })),
    ]).then(([v, l, s]) => {
      setVehicles(Array.isArray(v?.data) ? v.data : []);
      setLocations(Array.isArray(l?.data) ? l.data : []);
      const cfg = (s?.data && Array.isArray(s.data.booking_sources) && s.data.booking_sources.length)
        ? s.data.booking_sources : DEFAULT_SOURCES;
      setSources(cfg);
      setBusiness(s?.data || {});
    });
  }, [open]);

  // Refetch extras whenever the selected vehicle changes so we always show
  // the Car Group's allowed extras (falls back to all active).
  useEffect(() => {
    if (!open || !form?.vehicle_id) { setExtras([]); return; }
    api.get(`/extras?status=active&vehicle_id=${form.vehicle_id}`)
      .then((r) => setExtras(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExtras([]));
  }, [open, form?.vehicle_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAddon = (id) => setForm((f) => ({
    ...f, addons: f.addons.includes(id) ? f.addons.filter((a) => a !== id) : [...f.addons, id],
  }));

  // Live server-driven preview — debounced 300ms POST to /bookings/quote so
  // Subtotal/Extras/Tax/Total/Deposit/Balance Due always reflect the true
  // pricing engine (VAT-inclusive, seasonal rates, location fees, vouchers,
  // driver-age surcharges, loyalty). Same source of truth as the persisted
  // booking — no client-side math, no 1¢ drift.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  useEffect(() => {
    if (!form || !form.vehicle_id || !form.pickup_date || !form.return_date) {
      setQuote(null); return;
    }
    if (new Date(form.return_date) <= new Date(form.pickup_date)) {
      setQuote(null); return;
    }
    const controller = new AbortController();
    setQuoteLoading(true);
    const t = setTimeout(() => {
      api.post("/bookings/quote", {
        vehicle_id:      form.vehicle_id,
        pickup_date:     form.pickup_date,
        return_date:     form.return_date,
        pickup_time:     form.pickup_time,
        return_time:     form.return_time,
        pickup_location: form.pickup_location,
        return_location: form.return_location,
        addons:          form.addons,
        driver_age:      form.driver_age === "" ? null : Number(form.driver_age),
      }, { signal: controller.signal })
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 300);
    return () => { controller.abort(); clearTimeout(t); };
  }, [
    form?.vehicle_id, form?.pickup_date, form?.return_date,
    form?.pickup_time, form?.return_time,
    form?.pickup_location, form?.return_location,
    JSON.stringify(form?.addons), form?.driver_age,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form?.pickup_date || !form?.return_date) {
      setError("Please set both pickup and return dates."); return;
    }
    if (new Date(form.return_date) <= new Date(form.pickup_date)) {
      setError("Return date must be after pickup date."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        payment_method: form.payment_method || null,
        driver_age: form.driver_age === "" ? null : Number(form.driver_age),
        paid_amount: form.paid_amount === "" || form.paid_amount === null
          ? null
          : Number(form.paid_amount),
      };
      const { data } = await api.put(`/admin/bookings/${booking.id}`, payload);
      toast.success(`Booking updated · €${data.total?.toFixed?.(2) ?? data.total}`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !form) return null;

  // v4.6.6 — approve/decline a booking request from the detail modal.
  const decideRequest = async (action) => {
    setError("");
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/${action}-request`);
      toast.success(action === "approve"
        ? "Request approved — confirmation email sent."
        : "Request declined — the customer has been notified.");
      onSaved?.(data);
      onClose();
    } catch (err) {
      const detail = formatApiError(err?.response?.data?.detail) || err.message;
      setError(detail);
      toast.error(detail);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="edit-booking-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-4xl w-full p-8 max-h-[95vh] overflow-y-auto rounded border border-black/10 dark:border-white/10 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
              Booking #{booking.id.slice(0, 8).toUpperCase()}
            </p>
            <h2 className="font-display text-3xl tracking-tight mt-1">Edit reservation</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-black dark:hover:text-white"
            aria-label="Close" data-testid="close-edit-modal"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <Section title="Customer">
            <Row>
              <Field label="Full name"><Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} testId="eb-fullname" /></Field>
              <Field label="Email"><Input type="email" value={form.user_email} onChange={(e) => update("user_email", e.target.value)} testId="eb-email" /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} testId="eb-phone" /></Field>
              <Field label="ID / Passport"><Input value={form.id_passport} onChange={(e) => update("id_passport", e.target.value)} testId="eb-id" /></Field>
              <Field label="Driver license"><Input value={form.license_number} onChange={(e) => update("license_number", e.target.value)} testId="eb-license" /></Field>
              <Field label="Driver age"><Input type="number" min="18" max="99" value={form.driver_age}
                onChange={(e) => update("driver_age", e.target.value)} testId="eb-driver-age" /></Field>
            </Row>
            {/* v4.0.3 — Dynamic Address / Country / City / ZIP / Notes
                rendered INSIDE the Customer card (previously lived below
                Rental). */}
            <div className="mt-4">
              <CheckoutFields
                form={form}
                setForm={setForm}
                locations={locations}
                renderGroup="customer"
                hideBasics
                hideDriver
              />
            </div>
          </Section>

          {/* Conditional airport / port / hotel fields — mirrors the
              customer checkout so the admin edit form respects the same
              REQUIRED / OPTIONAL matrix and location-type gating.
              Rendered BELOW the Rental card in its own dedicated
              container. */}

          <Section title="Rental">
            <Row>
              <Field label="Vehicle">
                <Select value={form.vehicle_id} onChange={(e) => update("vehicle_id", e.target.value)} testId="eb-vehicle">
                  {vehicles.map((v) => {
                    const rate = v.has_duration_tiers ? v.display_rate : v.daily_rate;
                    return <option key={v.id} value={v.id}>{v.brand} {v.name} — €{rate}/day</option>;
                  })}
                </Select>
              </Field>
              <Field label="Pickup location">
                <Select value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} testId="eb-pickup-loc">
                  {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                </Select>
              </Field>
              <Field label="Pickup date"><DateInput value={form.pickup_date} onChange={(v) => update("pickup_date", v)} testId="eb-pickup-date" /></Field>
              <Field label="Pickup time"><Input type="time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} testId="eb-pickup-time" /></Field>
              <Field label="Return location">
                <Select value={form.return_location} onChange={(e) => update("return_location", e.target.value)} testId="eb-return-loc">
                  {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                </Select>
              </Field>
              <Field label="Return date"><DateInput value={form.return_date} onChange={(v) => update("return_date", v)} testId="eb-return-date" /></Field>
              <Field label="Return time"><Input type="time" value={form.return_time} onChange={(e) => update("return_time", e.target.value)} testId="eb-return-time" /></Field>
            </Row>
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-2">
                Extras (filtered to this vehicle&apos;s group)
              </p>
              {extras.length === 0 ? (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 italic">
                  No extras configured for this Car Group.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="eb-extras-list">
                  {extras.map((x) => {
                    const on = form.addons.includes(x.id);
                    return (
                      <button type="button" key={x.id} onClick={() => toggleAddon(x.id)}
                        data-testid={`eb-addon-${x.id}`}
                        title={`€${Number(x.price).toFixed(2)} ${x.pricing_type === "per_day" ? "per day" : "per reservation"}`}
                        className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                          on ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                             : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
                        }`}
                      >
                        {x.name}
                        <span className="ml-1 opacity-70">€{Number(x.price).toFixed(0)}/{x.pricing_type === "per_day" ? "day" : "res"}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Section>

          <CheckoutFields
            form={form}
            setForm={setForm}
            locations={locations}
            renderGroup="conditional"
            hideBasics
            hideDriver
          />

          <PortFerryCard
            pickupLocation={form.pickup_location}
            value={form.ferry_details}
            onChange={(next) => update("ferry_details", next)}
            testIdPrefix="eb"
          />

          <Section title="Status & payment">
            <Row>
              <Field label="Source">
                <Select value={form.source} onChange={(e) => update("source", e.target.value)} testId="eb-source">
                  {sources.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>)}
                </Select>
              </Field>
              <Field label="Booking status">
                <Select value={form.status} onChange={(e) => update("status", e.target.value)} testId="eb-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </Select>
              </Field>
              <Field label="Payment status">
                <Select value={form.payment_status} onChange={(e) => update("payment_status", e.target.value)} testId="eb-payment-status">
                  {PAYMENT_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Select>
              </Field>
              <Field label="Payment method">
                <Select value={form.payment_method || ""} onChange={(e) => update("payment_method", e.target.value)} testId="eb-payment-method">
                  {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Select>
              </Field>
              <Field label={`Deposit received (€${Number(quote?.total ?? booking.total ?? 0).toFixed(2)} max)`}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.paid_amount}
                  onChange={(e) => update("paid_amount", e.target.value)}
                  testId="eb-paid-amount"
                />
              </Field>
            </Row>
            {(() => {
              const total = Number(booking.total || 0);
              const paid = Number(booking.paid_amount || 0);
              const showCollect =
                booking.payment_status === "partial" ||
                (paid > 0 && paid < total);
              if (!showCollect) return null;
              return (
                <p
                  className="mt-3 text-[11px] text-amber-800 dark:text-amber-300"
                  data-testid="eb-balance-hint"
                >
                  Partial payment on file — see the summary panel below to
                  mark the balance collected at pickup.
                </p>
              );
            })()}
            {/* v4.0.3 — "Notes (internal)" retired: the Notes field now
                lives inside the Customer card via CheckoutFields. */}
            <div className="pt-3 flex flex-wrap items-center gap-3 text-xs">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/bookings/${booking.id}/resend-confirmation`);
                    if (data.email_sent) {
                      toast.success("Confirmation email sent", {
                        description: data.email_has_pdf ? "Includes PDF agreement." : "PDF attachment skipped.",
                      });
                    } else if (data.email_status === "mock_no_key") {
                      toast.warning("Resend API key not configured", {
                        description: "Add RESEND_API_KEY in backend/.env to send real emails.",
                      });
                    } else {
                      toast.error("Send failed", { description: data.email_status });
                    }
                  } catch (err) {
                    toast.error("Send failed", {
                      description: formatApiError(err?.response?.data?.detail) || err.message,
                    });
                  }
                }}
                className="inline-flex items-center gap-1 border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white px-3 py-1.5 rounded"
                data-testid="eb-resend-email"
              >
                Resend confirmation email
              </button>
              {booking.email_sent && (
                <span className="text-emerald-700 dark:text-emerald-400" data-testid="eb-email-status">
                  {(() => {
                    const raw = booking.email_sent_at || booking.updated_at || booking.created_at;
                    const d = raw ? new Date(raw) : null;
                    if (!d || Number.isNaN(d.getTime())) return "Sent";
                    const pad = (n) => String(n).padStart(2, "0");
                    return `Sent: ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} at ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                  })()}
                </span>
              )}
              {!booking.email_sent && booking.email_status && (
                <span className="text-amber-700 dark:text-amber-400">
                  {booking.email_status}
                </span>
              )}
            </div>
          </Section>

          {/* Pricing summary — server-authoritative live quote. Order per
              spec: Subtotal → Extras → Tax → Estimated Total → Balance Due
              → Actions. */}
          {quote && (
            <div
              className="bg-black/[0.02] dark:bg-white/[0.03] border border-black/10 dark:border-white/10 rounded p-4 text-sm space-y-1"
              data-testid="eb-preview"
            >
              <div className="flex justify-between"><span>Days</span><span>{quote.days}</span></div>
              <div className="flex justify-between"><span>Subtotal (rental)</span><span>€{Number(quote.subtotal || 0).toFixed(2)}</span></div>
              {Number(quote.addons_total || 0) > 0 && (
                <div className="flex justify-between"><span>Extras</span><span>€{Number(quote.addons_total).toFixed(2)}</span></div>
              )}
              {Number(quote.pickup_fee || 0) + Number(quote.return_fee || 0) + Number(quote.out_of_hours_fee || 0) > 0 && (
                <div className="flex justify-between text-neutral-500">
                  <span>Location & out-of-hours fees</span>
                  <span>€{(Number(quote.pickup_fee || 0) + Number(quote.return_fee || 0) + Number(quote.out_of_hours_fee || 0)).toFixed(2)}</span>
                </div>
              )}
              {Number(quote.voucher_discount || 0) > 0 && (
                <div className="flex justify-between text-emerald-700 dark:text-emerald-400">
                  <span>Voucher {quote.voucher_code}</span><span>− €{Number(quote.voucher_discount).toFixed(2)}</span>
                </div>
              )}
              {Number(quote.loyalty_discount || 0) > 0 && (
                <div className="flex justify-between text-emerald-700 dark:text-emerald-400">
                  <span>Loyalty discount</span><span>− €{Number(quote.loyalty_discount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-neutral-500">
                <span>VAT ({business?.tax_rate_pct ?? 24}% included)</span>
                <span>€{Number(quote.taxes || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-1 border-t border-black/10 dark:border-white/10 mt-1 text-base">
                <span>Estimated total</span>
                <span data-testid="eb-preview-total">€{Number(quote.total || 0).toFixed(2)}</span>
              </div>
              {quoteLoading && (
                <p className="text-[10px] text-neutral-500 italic pt-1" data-testid="eb-quote-loading">Recalculating…</p>
              )}
            </div>
          )}

          {/* Balance-due box — always below the totals block, before the
              action row. Uses the LIVE form.paid_amount so the admin sees
              the balance recompute as they edit the deposit input. */}
          {(() => {
            const total = Number(quote?.total ?? booking.total ?? 0);
            const paidRaw = form.paid_amount === "" ? 0 : Number(form.paid_amount);
            const paid = Math.max(0, Math.min(Number.isFinite(paidRaw) ? paidRaw : 0, total));
            const balance = Math.max(total - paid, 0);
            if (total <= 0) return null;
            if (balance <= 0 && paid < total) return null;
            const persistedPaid = Number(booking.paid_amount || 0);
            const isDirty = Math.abs(paid - persistedPaid) > 0.005;
            return (
              <div
                className="p-4 border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 rounded flex items-center justify-between gap-3"
                data-testid="eb-balance-panel"
              >
                <div className="text-sm">
                  <p className="font-medium text-amber-900 dark:text-amber-200">
                    Balance due at pickup: €{balance.toFixed(2)}
                  </p>
                  <p className="text-[11px] text-amber-800/80 dark:text-amber-300/70 mt-0.5">
                    Deposit €{paid.toFixed(2)} {isDirty ? "(unsaved)" : "received"} · Total €{total.toFixed(2)}
                  </p>
                </div>
                {balance > 0 && !isDirty && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (saving) return;
                      setSaving(true);
                      setError("");
                      try {
                        await api.post(`/admin/bookings/${booking.id}/mark-balance-collected`);
                        toast.success(`Balance €${balance.toFixed(2)} collected`);
                        onSaved?.({ ...booking, payment_status: "paid", paid_amount: total });
                        onClose();
                      } catch (err) {
                        setError(formatApiError(err?.response?.data?.detail) || err.message);
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="bg-emerald-700 text-white px-4 py-2 text-xs rounded hover:bg-emerald-800 disabled:opacity-50 whitespace-nowrap"
                    data-testid="eb-mark-balance-collected"
                  >
                    Mark balance collected
                  </button>
                )}
              </div>
            );
          })()}

          <HandoverAudit booking={booking} />

          {error && <p className="text-red-600 dark:text-rose-400 text-sm" data-testid="eb-error">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            {booking.status === "pending" && (
              <>
                <button
                  type="button"
                  onClick={() => decideRequest("approve")}
                  className="bg-emerald-600 text-white px-5 py-2 text-sm rounded hover:bg-emerald-700 inline-flex items-center gap-2 mr-auto"
                  data-testid="eb-approve-request"
                >
                  <CheckCircle2 size={14} /> Approve Request
                </button>
                <button
                  type="button"
                  onClick={() => decideRequest("decline")}
                  className="border border-rose-300 text-rose-700 dark:border-rose-800 dark:text-rose-300 px-4 py-2 text-sm rounded hover:bg-rose-50 dark:hover:bg-rose-900/30"
                  data-testid="eb-decline-request"
                >
                  Decline Request
                </button>
              </>
            )}
            <button type="button" onClick={onClose}
              className="border border-black/15 dark:border-white/15 px-5 py-2 text-sm hover:border-black dark:hover:border-white transition-colors rounded"
              data-testid="eb-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="bg-black text-white dark:bg-white dark:text-black px-5 py-2 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50 rounded"
              data-testid="eb-submit">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HandoverAudit({ booking }) {
  const handed = booking.status === "active" || !!booking.handed_over_at;
  const when = booking.handed_over_at
    ? new Date(booking.handed_over_at).toLocaleString([], {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";
  return (
    <div
      className="p-4 rounded border border-slate-200 dark:border-slate-600/60 bg-slate-50 dark:bg-slate-800/60"
      data-testid="eb-handover-audit"
    >
      <h4 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 dark:text-slate-400">
        Handover &amp; fleet operations
      </h4>
      {!handed ? (
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-2" data-testid="eb-handover-pending">
          Pending handover at desk
        </p>
      ) : (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-slate-800 dark:text-slate-100">
          <AuditCell label="Handed over by" value={booking.handed_over_by || "—"} />
          <AuditCell label="Date & time" value={when} />
          <AuditCell
            label="Odometer at handover"
            value={booking.handover_odometer ? `${Number(booking.handover_odometer).toLocaleString()} km` : "—"}
          />
          <AuditCell label="Fuel level" value={booking.handover_fuel_level || "—"} />
          {booking.handover_notes && (
            <div className="sm:col-span-2">
              <AuditCell label="Handover notes" value={booking.handover_notes} />
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

function AuditCell({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-5">
      <h3 className="font-display text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}
function Row({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-1">{label}</span>
      {children}
    </label>
  );
}
function Input({ testId, ...rest }) {
  return <input {...rest} data-testid={testId}
    className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm" />;
}
function Select({ testId, children, ...rest }) {
  return (
    <select {...rest} data-testid={testId}
      className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm">
      {children}
    </select>
  );
}
